import { useState } from 'react';
import { C } from '../../lib/theme';
import { styles } from '../../lib/styles';
import type { Case } from '../../domain/initial-cases';
import { Dialog } from '../Dialog';
import { useUIState } from '../../state/contexts/UIStateContext';
import { useDeletionActions } from '../../state/actions/deletion';

// #691 — the confirmation in front of the one irreversible thing staff can do
// to a registration. See the header of state/actions/deletion.ts for what the
// act IS and how it differs from the BCBP offboard.
//
// TYPE-THE-NAME rather than an "Are you sure?" button, and the reason is the
// CTIA consequence rather than the database one. A soft-deleted row can be
// restored by an operator with database access; an enterprise removed from the
// BCID platform has to be registered again and re-branded, and the registrant's
// calls stop being branded in the meantime. An OK button is one mis-aimed click
// away from that, and the operator has no way to tell afterwards which case
// they were on. Typing the registrant's legal name makes the identity of the
// target an input to the action, so the classic failure — the right gesture on
// the wrong row — cannot get through.
//
// EXACT MATCH, deliberately. Not trimmed, not case-folded, not
// whitespace-collapsed. Every softening turns a check on "did you read the row"
// into a check on "can you type approximately", and the name is on screen
// directly above the box to copy. The one accommodation is that the field is
// selectable text rather than an image.
export function DeleteRegistrationDialog({ c, onDeleted }: {
  c: Case;
  onDeleted?: () => void;
}) {
  const { setModal } = useUIState();
  const { deleteRegistration } = useDeletionActions();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  // #691 — "live at CTIA", and it is A COPY OF THE SERVER'S OFFBOARD CONDITION.
  //
  // `DELETE /api/v1/cases/:id` enqueues the CTIA enterprise-delete when
  // `updated.settlementSyncStatus === 'SYNCED' || updated.settlementSyncStep
  // !== null` (apps/api/src/routes/cases.ts). This warning has to be the same
  // predicate, because what it promises the operator is precisely what that
  // branch will do. THE TWO MUST CHANGE TOGETHER; the API side is the authority
  // and this is the reading of it. `cases-delete.test.ts` pins that
  // `settlementSyncStep` is still served, which is the half of the coupling a
  // type cannot catch.
  //
  // THE SECOND CLAUSE IS NOT DECORATION, and getting this wrong is what the
  // first draft of this file did. `settlementSyncStep` records how far a
  // part-way sync got — ENTERPRISE_REGISTERED, DIR_UPLOADED, TNS_UPLOADED —
  // and a case can carry one while `settlementSyncStatus` still reads
  // PENDING_SYNC, because the status only becomes SYNCED when the worker
  // finishes. An enterprise IS registered at CTIA at that point, and the delete
  // WILL remove it. Warning on the status alone told that operator nothing.
  //
  // It is served, and that is measured rather than assumed: `caseApiColumns()`
  // is a wildcard over every column of `cases` bar one, and the case reads
  // carry no enumerated response schema, so the field reaches the SPA on both
  // `GET /cases/:id` and `GET /cases`. That is also why it is worth a test —
  // a projection that stops populating a field produces no OpenAPI drift.
  const liveAtCtia = c.settlementSyncStatus === 'SYNCED' || c.settlementSyncStep != null;

  const matches = typed === c.registrant;

  // ONE PREDICATE, read by the rendered `disabled` and by the handler.
  //
  // The handler's early return is a RESTATEMENT of the attribute, not a second
  // gate, and saying so matters: a disabled <button> dispatches no click, so
  // removing the early return on its own changes nothing observable and no test
  // can be written that it would fail. Sharing the name is what keeps that
  // honest — the two can no longer drift apart, and mutating `canConfirm` takes
  // both down together, which is how the guard is actually pinned. It is kept
  // rather than deleted for the caller that renders this button without the
  // attribute; it is not claimed to be independently tested.
  const canConfirm = matches && !busy;

  const confirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    const ok = await deleteRegistration(c);
    // Closed on both outcomes. The failure reason is a toast, and this dialog
    // is a native <dialog> in the top layer — leaving it open would cover the
    // very sentence explaining why the click did nothing.
    setModal(null);
    if (ok) onDeleted?.();
  };

  return (
    <Dialog
      title="Delete registration"
      titleStyle={{ color: C.danger }}
      onClose={() => setModal(null)}
    >
      <div style={{ display: 'grid', gap: 10, fontSize: 13, color: C.text }}>
        <div>
          This permanently removes the registration from the portal. It cannot be undone from here.
        </div>

        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', margin: 0 }}>
          <dt style={{ fontSize: 11, color: C.textMuted }}>Case</dt>
          <dd style={{ margin: 0, fontFamily: 'monospace', fontSize: 13 }}>{c.id}</dd>
          <dt style={{ fontSize: 11, color: C.textMuted }}>Registrant</dt>
          <dd style={{ margin: 0, fontWeight: 600 }}>{c.registrant}</dd>
          <dt style={{ fontSize: 11, color: C.textMuted }}>Status</dt>
          <dd style={{ margin: 0 }}>{c.status}</dd>
        </dl>

        {liveAtCtia && (
          <div
            role="alert"
            style={{
              borderLeft: `3px solid ${C.danger}`,
              background: C.dangerBg,
              color: C.danger,
              padding: '8px 12px',
              fontWeight: 600,
            }}
          >
            This registration is live at CTIA; deleting it will remove the enterprise from the BCID platform.
          </div>
        )}

        <div>
          <label htmlFor="delete-registration-confirm" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 4 }}>
            Type <strong style={{ color: C.text }}>{c.registrant}</strong> to confirm
          </label>
          <input
            id="delete-registration-confirm"
            style={{ ...styles.input, width: '100%' }}
            value={typed}
            autoComplete="off"
            disabled={busy}
            onChange={e => setTyped(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
        <button
          type="button"
          style={{ ...styles.btn, ...styles.btnDanger, ...(canConfirm ? {} : { opacity: 0.5, cursor: 'not-allowed' }) }}
          disabled={!canConfirm}
          onClick={() => { void confirm(); }}
        >{busy ? 'Deleting…' : 'Delete registration'}</button>
        <button
          type="button"
          style={{ ...styles.btn, ...styles.btnOutline }}
          disabled={busy}
          onClick={() => setModal(null)}
        >Cancel</button>
      </div>
    </Dialog>
  );
}
